import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import debounce from "@mui/utils/debounce";
import { scaleCssValue, createResponsiveScaler } from "../utils";
import { useTheme } from "@mui/material/styles";
import { BR_POINT_MOBILE, DEBOUNCE, ETALON_MQ, STEP } from "../constants";

export type StyleObject = Record<string, any>;
export type ResponsiveOverrides = { mobile?: StyleObject };

type ResponsiveScalerContextValue = {
  rpx: (styles: StyleObject, overrides?: ResponsiveOverrides) => StyleObject;
  isMobile: boolean;
  windowWidth: number;
};

const Index = createContext<ResponsiveScalerContextValue | null>(null);

export function ResponsiveScalerProvider(props: {
  children: React.ReactNode;
  etalonMQ?: number;
  step?: number;
  debounceMs?: number;
}) {
  const {
    children,
    etalonMQ = ETALON_MQ,
    step = STEP,
    debounceMs = DEBOUNCE
  } = props;
  const theme = useTheme();
  const mdBreakpoint = theme.breakpoints.values[BR_POINT_MOBILE];

  const [windowWidth, setWindowWidth] = useState(
    typeof window === "undefined" ? etalonMQ : window.innerWidth
  );

  const isMobile = windowWidth <= mdBreakpoint;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = debounce(() => {
      requestAnimationFrame(() => {
        setWindowWidth((prev) => {
          const next = window.innerWidth;
          return prev !== next ? next : prev;
        });
      });
    }, debounceMs);

    handler(); // init
    window.addEventListener("resize", handler);

    return () => {
      handler.clear?.();
      window.removeEventListener("resize", handler);
    };
  }, []);

  const scaleFn = useMemo(
    () => createResponsiveScaler(windowWidth, etalonMQ, step),
    [windowWidth, etalonMQ, step]
  );

  const rpx = useMemo(
    () =>
      (styles: StyleObject, overrides: ResponsiveOverrides = {}) => {
        if (isMobile && overrides.mobile) return overrides.mobile;

        const out: StyleObject = {};

        for (const key in styles) {
          out[key] = scaleCssValue(styles[key], scaleFn);
        }

        return out;
      },
    [isMobile, scaleFn]
  );

  return (
    <Index.Provider value={{ rpx, isMobile, windowWidth }}>
      {children}
    </Index.Provider>
  );
}

export function useResponsiveScaler() {
  const ctx = useContext(Index);
  if (!ctx) {
    throw new Error("useResponsiveScaler must be used inside ResponsiveScalerProvider");
  }
  return ctx;
}
